import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { get } from '../../../api/client';
import type { PaginationMeta } from '../../../types';

interface RecentOrder {
  id: string;
  orderNumber: string | null;
  orderCategory: string | null;
  orderCategoryId: string | null;
  category: { id: string; name: string | null } | null;
  orderStatus: string | null;
  orderStartDate: string;
  orderAmount: number | null;
  customer: { fullName: string | null; companyName: string | null } | null;
  serviceType: { name: string | null } | null;
}

const STATUS_STYLE: Record<string, string> = {
  '未执行': 'bg-amber-50 text-amber-600',
  '执行中': 'bg-cyan-50 text-cyan-600',
  '已完成': 'bg-teal-50 text-teal-600',
  '已取消': 'bg-rose-50 text-rose-500',
  '已归档': 'bg-gray-100 text-gray-500',
};

const STATUS_DOT: Record<string, string> = {
  '未执行': 'bg-amber-400',
  '执行中': 'bg-cyan-400',
  '已完成': 'bg-teal-400',
  '已取消': 'bg-rose-400',
  '已归档': 'bg-gray-400',
};

function formatDate(d: string): string {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
}

function formatAmount(v: number | null): string {
  if (v == null) return '-';
  return `¥${Number(v).toLocaleString('zh-CN', { minimumFractionDigits: 0 })}`;
}

export default function RecentOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<RecentOrder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    get<{ data: RecentOrder[], pagination: PaginationMeta }>('/orders', {
      params: {
        pageSize: 8,
        orderBy: 'createdAt',
        orderDirection: 'desc'
      }
    }).then(res => {
      if (res?.data) setOrders(res.data);
      setLoading(false);
    }).catch(error => {
      console.error('获取最近订单失败:', error);
      setLoading(false);
    });
  }, []);

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-5">
        <h3 className="text-sm md:text-base font-semibold text-gray-900">最近订单</h3>
        <button
          onClick={() => navigate('/orders')}
          className="text-xs md:text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap flex items-center gap-1"
        >
          查看全部
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-arrow-right-s-line text-sm" />
          </div>
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 md:py-14">
          <div className="w-10 h-10 flex items-center justify-center mb-2">
            <i className="ri-inbox-line text-3xl text-gray-200" />
          </div>
          <p className="text-sm text-gray-400 mb-3">暂无订单数据</p>
          <button
            onClick={() => navigate('/orders')}
            className="text-xs text-teal-500 hover:text-teal-600 cursor-pointer transition-colors"
          >
            前往新建订单 →
          </button>
        </div>
      ) : (
        <>
          {/* Mobile: card list */}
          <div className="md:hidden space-y-2.5">
            {orders.map((order) => (
              <div
                key={order.id}
                onClick={() => navigate('/orders', { state: { openOrderId: order.id } })}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-teal-50/60 transition-colors"
              >
                {/* Status dot */}
                <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[order.orderStatus ?? ''] ?? 'bg-gray-300'}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-mono text-gray-400 truncate">
                      {order.orderNumber ?? order.id.slice(0,8).toUpperCase()}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${STATUS_STYLE[order.orderStatus ?? ''] ?? 'bg-gray-100 text-gray-500'}`}>
                      {order.orderStatus}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {order.customer?.fullName ?? '-'}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {order.serviceType?.name ?? order.category?.name ?? order.orderCategory ?? '-'} · {formatDate(order.orderStartDate)}
                  </p>
                </div>
                <p className="text-sm font-semibold text-gray-900 shrink-0">{formatAmount(order.orderAmount)}</p>
              </div>
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  {['订单号', '服务类型', '客户', '日期', '订单金额', '状态'].map((h) => (
                    <th key={h} className="text-left text-xs font-medium text-gray-400 pb-3 whitespace-nowrap pr-4 last:pr-0">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr
                    key={order.id}
                    onClick={() => navigate('/orders', { state: { openOrderId: order.id } })}
                    className="border-b border-gray-50 hover:bg-teal-50/40 transition-colors cursor-pointer"
                  >
                    <td className="py-3 pr-4">
                      <span className="text-xs font-mono text-gray-500">
                        {order.orderNumber ?? order.id.slice(0,8).toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 text-sm text-gray-600 pr-4 max-w-[120px] truncate">
                      {order.serviceType?.name ?? order.category?.name ?? order.orderCategory ?? '-'}
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-sm text-gray-800 font-medium truncate max-w-[120px]">
                        {order.customer?.fullName ?? '-'}
                      </p>
                      {order.customer?.companyName && (
                        <p className="text-xs text-gray-400 truncate max-w-[120px]">{order.customer.companyName}</p>
                      )}
                    </td>
                    <td className="py-3 text-sm text-gray-500 pr-4 whitespace-nowrap">
                      {formatDate(order.orderStartDate)}
                    </td>
                    <td className="py-3 text-sm font-semibold text-gray-900 pr-4 whitespace-nowrap">
                      {formatAmount(order.orderAmount)}
                    </td>
                    <td className="py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1.5 w-fit ${STATUS_STYLE[order.orderStatus ?? ''] ?? 'bg-gray-100 text-gray-500'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[order.orderStatus ?? ''] ?? 'bg-gray-300'}`} />
                        {order.orderStatus ?? '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
